/**
 * ESC/POS Command Builder for Thermal Printers
 * Supports 58mm and 80mm thermal printers via Web Serial API
 * Three receipt types: Kitchen, Order, Delivery
 */

// ESC/POS Commands
const ESC = 0x1b;
const GS = 0x1d;
const LF = 0x0a;

export const CMD = {
  INIT: new Uint8Array([ESC, 0x40]),
  CUT: new Uint8Array([GS, 0x56, 0x00]),
  PARTIAL_CUT: new Uint8Array([GS, 0x56, 0x01]),
  FEED: (lines: number) => new Uint8Array([ESC, 0x64, lines]),
  ALIGN_LEFT: new Uint8Array([ESC, 0x61, 0x00]),
  ALIGN_CENTER: new Uint8Array([ESC, 0x61, 0x01]),
  ALIGN_RIGHT: new Uint8Array([ESC, 0x61, 0x02]),
  BOLD_ON: new Uint8Array([ESC, 0x45, 0x01]),
  BOLD_OFF: new Uint8Array([ESC, 0x45, 0x00]),
  DOUBLE_HEIGHT_ON: new Uint8Array([ESC, 0x21, 0x10]),
  DOUBLE_WIDTH_ON: new Uint8Array([ESC, 0x21, 0x20]),
  DOUBLE_ON: new Uint8Array([ESC, 0x21, 0x30]),
  NORMAL_SIZE: new Uint8Array([ESC, 0x21, 0x00]),
  UNDERLINE_ON: new Uint8Array([ESC, 0x2d, 0x01]),
  UNDERLINE_OFF: new Uint8Array([ESC, 0x2d, 0x00]),
  BEEP: new Uint8Array([ESC, 0x42, 0x03, 0x02]),
  OPEN_DRAWER: new Uint8Array([ESC, 0x70, 0x00, 0x19, 0xfa]),
};

const encoder = new TextEncoder();

function encode(text: string): Uint8Array {
  const map: Record<string, string> = {
    á: "a", à: "a", â: "a", ã: "a", é: "e", ê: "e", í: "i",
    ó: "o", ô: "o", õ: "o", ú: "u", ü: "u", ç: "c", ñ: "n",
    Á: "A", À: "A", Â: "A", Ã: "A", É: "E", Ê: "E", Í: "I",
    Ó: "O", Ô: "O", Õ: "O", Ú: "U", Ü: "U", Ç: "C", Ñ: "N",
  };
  const cleaned = text.replace(/[áàâãéêíóôõúüçñÁÀÂÃÉÊÍÓÔÕÚÜÇÑ]/g, (c) => map[c] || c);
  return encoder.encode(cleaned);
}

function concat(...arrays: Uint8Array[]): Uint8Array {
  const totalLength = arrays.reduce((sum, a) => sum + a.length, 0);
  const result = new Uint8Array(totalLength);
  let offset = 0;
  for (const arr of arrays) { result.set(arr, offset); offset += arr.length; }
  return result;
}

function line(text: string): Uint8Array { return concat(encode(text), new Uint8Array([LF])); }
function separator(width: number = 48): Uint8Array { return line("-".repeat(width)); }
function padLine(left: string, right: string, width: number = 48): Uint8Array {
  const padding = Math.max(1, width - left.length - right.length);
  return line(left + " ".repeat(padding) + right);
}

export type PrinterSize = "58mm" | "80mm";
function getWidth(size: PrinterSize): number { return size === "58mm" ? 32 : 48; }
const formatCurrency = (v: number) => `R$ ${v.toFixed(2).replace(".", ",")}`;

// ===== Shared types =====

export interface PrintItemAddon {
  categoryName?: string;
  name: string;
  quantity: number;
  price: number;
}

export interface PrintItem {
  name: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  notes?: string | null;
  addons?: PrintItemAddon[];
}

export interface CustomerInfo {
  name?: string | null;
  phone?: string | null;
  totalOrders?: number; // 0 = first time
  loyaltyPoints?: number | null;
}

export interface RestaurantInfo {
  name: string;
  phone?: string | null;
  cnpj?: string | null;
  address?: string | null;
  slug?: string | null;
  receiptThankYouMessage?: string | null;
}

export const PAYMENT_LABELS: Record<string, string> = {
  pix: "PIX", credit_card: "Cartao Credito", debit_card: "Cartao Debito",
  cash: "Dinheiro", mercadopago: "MercadoPago", pay_on_delivery: "Pagar na Entrega",
};

export const ORDER_TYPE_LABELS: Record<string, string> = {
  dine_in: "Mesa", takeout: "Retirada", delivery: "Delivery", scheduled: "Agendado",
};

// ===== Kitchen Ticket =====

export interface KitchenTicketData {
  orderId: string;
  orderType: string;
  tableNumber?: number | null;
  customer: CustomerInfo;
  waiterName?: string | null;
  items: PrintItem[];
  notes?: string | null;
  createdAt: string;
  scheduledFor?: string | null;
  paymentStatus?: string | null;
  paymentMethod?: string | null;
}

export function buildKitchenTicket(data: KitchenTicketData, size: PrinterSize = "80mm"): Uint8Array {
  const w = getWidth(size);
  const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
  const parts: Uint8Array[] = [
    CMD.INIT, CMD.BEEP,
    CMD.ALIGN_CENTER, CMD.DOUBLE_ON, line("** COZINHA **"), CMD.NORMAL_SIZE,
    separator(w), CMD.ALIGN_LEFT,
    CMD.BOLD_ON, line(`PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}`), CMD.BOLD_OFF,
    line(`${new Date(data.createdAt).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}`),
    line(`Tipo: ${ORDER_TYPE_LABELS[data.orderType] || data.orderType}${data.tableNumber ? ` ${data.tableNumber}` : ""}`),
  ];

  if (data.customer.name) parts.push(line(`Cliente: ${data.customer.name}`));
  if (data.waiterName) parts.push(line(`Garcom: ${data.waiterName}`));

  // Scheduled
  if (data.scheduledFor) {
    parts.push(CMD.BOLD_ON, line(`AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}`), CMD.BOLD_OFF);
  }

  // Payment status
  const payStatus = data.paymentStatus === "paid" ? "PAGO" : "NAO PAGO";
  parts.push(line(`Pagamento: ${payStatus}${data.paymentMethod ? ` (${PAYMENT_LABELS[data.paymentMethod] || data.paymentMethod})` : ""}`));

  // Customer history
  if (isFirstTime) {
    parts.push(separator(w), CMD.BOLD_ON, CMD.ALIGN_CENTER,
      line("*** PRIMEIRA COMPRA ***"), CMD.ALIGN_LEFT, CMD.BOLD_OFF);
  } else if (data.customer.totalOrders && data.customer.totalOrders > 0) {
    parts.push(line(`Cliente recorrente (${data.customer.totalOrders} pedidos)`));
  }

  parts.push(separator(w));

  // Items with addons
  for (const item of data.items) {
    parts.push(CMD.BOLD_ON, CMD.DOUBLE_HEIGHT_ON,
      line(`${item.quantity}x ${item.name}`), CMD.NORMAL_SIZE, CMD.BOLD_OFF);
    if (item.addons && item.addons.length > 0) {
      const grouped = groupAddonsByCategory(item.addons);
      for (const [cat, addons] of Object.entries(grouped)) {
        if (cat) parts.push(line(`  [${cat}]`));
        for (const a of addons) {
          parts.push(line(`   + ${a.quantity > 1 ? `${a.quantity}x ` : ""}${a.name}`));
        }
      }
    }
    if (item.notes) parts.push(line(`   >> ${item.notes}`));
  }

  if (data.notes) {
    parts.push(separator(w), CMD.BOLD_ON, line(`OBS: ${data.notes}`), CMD.BOLD_OFF);
  }

  parts.push(separator(w), CMD.FEED(3), CMD.PARTIAL_CUT);
  return concat(...parts);
}

// ===== Order Receipt =====

export interface ReceiptData {
  restaurant: RestaurantInfo;
  orderId: string;
  orderType: string;
  tableNumber?: number | null;
  customer: CustomerInfo;
  waiterName?: string | null;
  paymentMethod?: string | null;
  paymentStatus?: string | null;
  items: PrintItem[];
  subtotal: number;
  deliveryFee?: number;
  discount?: number;
  total: number;
  notes?: string | null;
  changeAmount?: number | null;
  createdAt: string;
  scheduledFor?: string | null;
  deliveryAddress?: string | null;
  ifoodReference?: string | null;
}

export function buildReceipt(data: ReceiptData, size: PrinterSize = "80mm"): Uint8Array {
  const w = getWidth(size);
  const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
  const parts: Uint8Array[] = [
    CMD.INIT, CMD.ALIGN_CENTER, CMD.DOUBLE_ON,
    line(data.restaurant.name), CMD.NORMAL_SIZE,
    ...(data.restaurant.phone ? [line(data.restaurant.phone)] : []),
    ...(data.restaurant.cnpj ? [line(`CNPJ: ${data.restaurant.cnpj}`)] : []),
    ...(data.restaurant.address ? [line(data.restaurant.address)] : []),
    separator(w), CMD.ALIGN_LEFT,
    CMD.BOLD_ON, line(`PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}`), CMD.BOLD_OFF,
    line(`Data: ${new Date(data.createdAt).toLocaleString("pt-BR")}`),
    line(`Tipo: ${ORDER_TYPE_LABELS[data.orderType] || data.orderType}${data.tableNumber ? ` ${data.tableNumber}` : ""}`),
  ];

  if (data.customer.name) parts.push(line(`Cliente: ${data.customer.name}`));
  if (data.customer.phone && data.orderType === "delivery") parts.push(line(`Tel: ${data.customer.phone}`));
  if (data.waiterName) parts.push(line(`Garcom: ${data.waiterName}`));
  if (data.ifoodReference) parts.push(line(`iFood Ref: ${data.ifoodReference}`));

  if (data.scheduledFor) {
    parts.push(CMD.BOLD_ON, line(`AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}`), CMD.BOLD_OFF);
  }

  // Customer history
  if (isFirstTime) {
    parts.push(CMD.BOLD_ON, line(">> PRIMEIRA COMPRA DO CLIENTE <<"), CMD.BOLD_OFF);
  } else if (data.customer.totalOrders && data.customer.totalOrders > 0) {
    parts.push(line(`Cliente recorrente (${data.customer.totalOrders} pedidos)`));
  }
  if (data.customer.loyaltyPoints && data.customer.loyaltyPoints > 0) {
    parts.push(line(`Pontos acumulados: ${data.customer.loyaltyPoints}`));
  }

  // Delivery info
  if (data.orderType === "delivery" && data.deliveryAddress) {
    parts.push(separator(w), CMD.BOLD_ON, line("ENDERECO DE ENTREGA:"), CMD.BOLD_OFF,
      line(data.deliveryAddress));
  }

  parts.push(separator(w), CMD.BOLD_ON, padLine("ITEM", "VALOR", w), CMD.BOLD_OFF, separator(w));

  for (const item of data.items) {
    parts.push(padLine(`${item.quantity}x ${item.name.substring(0, w - 15)}`, formatCurrency(item.totalPrice), w));
    if (item.addons && item.addons.length > 0) {
      const grouped = groupAddonsByCategory(item.addons);
      for (const [cat, addons] of Object.entries(grouped)) {
        if (cat) parts.push(line(`  [${cat}]`));
        for (const a of addons) {
          const addonPrice = a.price > 0 ? ` +${formatCurrency(a.price * a.quantity)}` : "";
          parts.push(line(`  + ${a.quantity > 1 ? `${a.quantity}x ` : ""}${a.name}${addonPrice}`));
        }
      }
    }
    if (item.notes) parts.push(line(`  * ${item.notes.substring(0, w - 4)}`));
  }

  parts.push(separator(w), padLine("Subtotal", formatCurrency(data.subtotal), w));
  if (data.deliveryFee && data.deliveryFee > 0) parts.push(padLine("Taxa entrega", formatCurrency(data.deliveryFee), w));
  if (data.discount && data.discount > 0) parts.push(padLine("Desconto", `-${formatCurrency(data.discount)}`, w));

  parts.push(separator(w), CMD.BOLD_ON, CMD.DOUBLE_HEIGHT_ON,
    padLine("TOTAL", formatCurrency(data.total), w),
    CMD.NORMAL_SIZE, CMD.BOLD_OFF, separator(w),
    line(`Pagamento: ${PAYMENT_LABELS[data.paymentMethod || ""] || data.paymentMethod || "---"}`),
    ...(data.paymentStatus ? [line(`Status: ${data.paymentStatus === "paid" ? "PAGO" : "PENDENTE"}`)] : []),
  );

  if (data.changeAmount && data.changeAmount > 0) {
    parts.push(CMD.BOLD_ON, line(`TROCO: ${formatCurrency(data.changeAmount)}`), CMD.BOLD_OFF);
  }
  if (data.notes) parts.push(separator(w), line(`Obs: ${data.notes.substring(0, w * 2)}`));

  parts.push(separator(w), CMD.ALIGN_CENTER, line("Obrigado pela preferencia!"),
    CMD.FEED(4), CMD.PARTIAL_CUT);

  return concat(...parts);
}

// ===== Delivery Receipt (for the driver / customer copy) =====

export interface DeliveryReceiptData extends ReceiptData {
  // same as ReceiptData but with thank you message logic
}

export function buildDeliveryReceipt(data: DeliveryReceiptData, size: PrinterSize = "80mm"): Uint8Array {
  const w = getWidth(size);
  const isFirstTime = (data.customer.totalOrders ?? 0) === 0;
  const parts: Uint8Array[] = [
    CMD.INIT, CMD.ALIGN_CENTER, CMD.DOUBLE_ON,
    line(data.restaurant.name), CMD.NORMAL_SIZE,
    ...(data.restaurant.phone ? [line(data.restaurant.phone)] : []),
    ...(data.restaurant.cnpj ? [line(`CNPJ: ${data.restaurant.cnpj}`)] : []),
    ...(data.restaurant.address ? [line(data.restaurant.address)] : []),
    separator(w), CMD.ALIGN_LEFT,
    CMD.BOLD_ON, line(`PEDIDO #${data.orderId.slice(0, 8).toUpperCase()}`), CMD.BOLD_OFF,
    line(`Data: ${new Date(data.createdAt).toLocaleString("pt-BR")}`),
    line(`Tipo: ${ORDER_TYPE_LABELS[data.orderType] || data.orderType}`),
  ];

  if (data.customer.name) parts.push(line(`Cliente: ${data.customer.name}`));
  if (data.customer.phone) parts.push(line(`Tel: ${data.customer.phone}`));
  if (data.ifoodReference) parts.push(line(`iFood Ref: ${data.ifoodReference}`));

  if (data.scheduledFor) {
    parts.push(CMD.BOLD_ON, line(`AGENDADO: ${new Date(data.scheduledFor).toLocaleString("pt-BR")}`), CMD.BOLD_OFF);
  }

  // Customer history
  if (isFirstTime) {
    parts.push(CMD.BOLD_ON, line(">> PRIMEIRA COMPRA DO CLIENTE <<"), CMD.BOLD_OFF);
  } else if (data.customer.totalOrders && data.customer.totalOrders > 0) {
    parts.push(line(`Cliente recorrente (${data.customer.totalOrders} pedidos)`));
  }
  if (data.customer.loyaltyPoints && data.customer.loyaltyPoints > 0) {
    parts.push(line(`Pontos acumulados: ${data.customer.loyaltyPoints}`));
  }

  // Delivery address
  if (data.deliveryAddress) {
    parts.push(separator(w), CMD.BOLD_ON, line("ENDERECO DE ENTREGA:"), CMD.BOLD_OFF,
      line(data.deliveryAddress));
  }

  parts.push(separator(w), CMD.BOLD_ON, padLine("ITEM", "VALOR", w), CMD.BOLD_OFF, separator(w));

  for (const item of data.items) {
    parts.push(padLine(`${item.quantity}x ${item.name.substring(0, w - 15)}`, formatCurrency(item.totalPrice), w));
    if (item.addons && item.addons.length > 0) {
      const grouped = groupAddonsByCategory(item.addons);
      for (const [cat, addons] of Object.entries(grouped)) {
        if (cat) parts.push(line(`  [${cat}]`));
        for (const a of addons) {
          const addonPrice = a.price > 0 ? ` +${formatCurrency(a.price * a.quantity)}` : "";
          parts.push(line(`  + ${a.quantity > 1 ? `${a.quantity}x ` : ""}${a.name}${addonPrice}`));
        }
      }
    }
    if (item.notes) parts.push(line(`  * ${item.notes.substring(0, w - 4)}`));
  }

  parts.push(separator(w), padLine("Subtotal", formatCurrency(data.subtotal), w));
  if (data.deliveryFee && data.deliveryFee > 0) parts.push(padLine("Taxa entrega", formatCurrency(data.deliveryFee), w));
  if (data.discount && data.discount > 0) parts.push(padLine("Desconto", `-${formatCurrency(data.discount)}`, w));

  parts.push(separator(w), CMD.BOLD_ON, CMD.DOUBLE_HEIGHT_ON,
    padLine("TOTAL", formatCurrency(data.total), w),
    CMD.NORMAL_SIZE, CMD.BOLD_OFF, separator(w),
    line(`Pagamento: ${PAYMENT_LABELS[data.paymentMethod || ""] || data.paymentMethod || "---"}`),
    ...(data.paymentStatus ? [line(`Status: ${data.paymentStatus === "paid" ? "PAGO" : "PENDENTE"}`)] : []),
  );

  if (data.changeAmount && data.changeAmount > 0) {
    parts.push(CMD.BOLD_ON, line(`TROCO: ${formatCurrency(data.changeAmount)}`), CMD.BOLD_OFF);
  }
  if (data.notes) parts.push(separator(w), line(`Obs: ${data.notes.substring(0, w * 2)}`));

  // Thank you message (customizable)
  if (data.restaurant.receiptThankYouMessage) {
    parts.push(separator(w), CMD.ALIGN_CENTER, line(data.restaurant.receiptThankYouMessage));
  }

  parts.push(CMD.FEED(4), CMD.PARTIAL_CUT);
  return concat(...parts);
}

// ===== Cash Report (unchanged) =====

export interface CashReportData {
  restaurantName: string;
  openedAt: string;
  closedAt: string;
  openingBalance: number;
  totalSales: number;
  salesCount: number;
  totalSangria: number;
  totalSuprimento: number;
  totalChangeGiven: number;
  expectedBalance: number;
  closingBalance: number;
  difference: number;
}

export function buildCashReport(data: CashReportData, size: PrinterSize = "80mm"): Uint8Array {
  const w = getWidth(size);
  const parts: Uint8Array[] = [
    CMD.INIT, CMD.ALIGN_CENTER, CMD.DOUBLE_ON,
    line(data.restaurantName), CMD.NORMAL_SIZE,
    CMD.BOLD_ON, line("FECHAMENTO DE CAIXA"), CMD.BOLD_OFF,
    separator(w), CMD.ALIGN_LEFT,
    line(`Abertura: ${data.openedAt}`),
    line(`Fechamento: ${data.closedAt}`),
    separator(w),
    padLine("Saldo Inicial", formatCurrency(data.openingBalance), w),
    padLine(`Vendas (${data.salesCount})`, `+${formatCurrency(data.totalSales)}`, w),
    padLine("Suprimentos", `+${formatCurrency(data.totalSuprimento)}`, w),
    padLine("Sangrias", `-${formatCurrency(data.totalSangria)}`, w),
    padLine("Trocos", `-${formatCurrency(data.totalChangeGiven)}`, w),
    separator(w), CMD.BOLD_ON,
    padLine("Saldo Esperado", formatCurrency(data.expectedBalance), w),
    padLine("Saldo Informado", formatCurrency(data.closingBalance), w),
    padLine("Diferenca", `${data.difference >= 0 ? "+" : ""}${formatCurrency(data.difference)}`, w),
    CMD.BOLD_OFF, separator(w), CMD.ALIGN_CENTER,
    line("Relatorio gerado automaticamente"),
    CMD.FEED(4), CMD.PARTIAL_CUT,
  ];
  return concat(...parts);
}

// ===== Helpers =====

function groupAddonsByCategory(addons: PrintItemAddon[]): Record<string, PrintItemAddon[]> {
  const map: Record<string, PrintItemAddon[]> = {};
  for (const a of addons) {
    const cat = a.categoryName || "";
    if (!map[cat]) map[cat] = [];
    map[cat].push(a);
  }
  return map;
}
